import React, { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import PageShell from '../components/Layout/PageShell'
import PortalHeader from '../components/Layout/PortalHeader'
import { PORTAL_ROLES } from '../components/Sidebar/portalNav'
import UpgradePricingBuilder from '../components/Pricing/UpgradePricingBuilder'
import StaticWeChatPayModal from '../components/Payment/StaticWeChatPayModal'
import { RsIcon } from '../components/icons'
import axios from 'axios'
import toast from 'react-hot-toast'

const PLAN_UI = [
  { icon: 'star', color: 'blue', popular: false },
  { icon: 'zap', color: 'gold', popular: true },
  { icon: 'building2', color: 'purple', popular: false },
]

function formatRenewDate(value) {
  if (!value) return '—'
  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('zh-CN')
}

function buildComparisonRows(planList) {
  const tiers = planList.filter((p) => ['tier1', 'tier2', 'tier3'].includes(p.id))
  const pick = (id) => tiers.find((p) => p.id === id)
  const t1 = pick('tier1')
  const t2 = pick('tier2')
  const t3 = pick('tier3')
  return [
    { feature: 'Monthly price', free: '¥0', basic: t1 ? `¥${t1.price}` : '—', premium: t2 ? `¥${t2.price}` : '—', enterprise: t3 ? `¥${t3.price}` : '—' },
    { feature: 'Contacts / month', free: 'Browse only', basic: t1 ? String(t1.contactQuota) : '—', premium: t2 ? String(t2.contactQuota) : '—', enterprise: t3 ? String(t3.contactQuota) : '—' },
    { feature: 'Company seats', free: '—', basic: t1 ? String(t1.companySeats) : '—', premium: t2 ? String(t2.companySeats) : '—', enterprise: t3 ? String(t3.companySeats) : '—' },
    { feature: 'Email campaigns', free: '❌', basic: 'With approval', premium: 'With approval', enterprise: 'With approval' },
    { feature: 'Chat with GP/LP', free: '❌', basic: '✅', premium: '✅', enterprise: '✅' },
    { feature: 'Document translation', free: '❌', basic: '❌', premium: '✅', enterprise: '✅' },
    { feature: 'Tencent Meetings', free: '❌', basic: '❌', premium: '✅', enterprise: '✅' },
  ]
}

export default function Payment() {
  const { user, isPaid, refreshUser, role } = useAuth()
  const isPortal = PORTAL_ROLES.includes(role)
  const useUpgradeBuilder = role === 'GP' || role === 'LP' || role === 'Startup'
  const [searchParams] = useSearchParams()
  const [plans, setPlans] = useState([])
  const [payOrder, setPayOrder] = useState(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (searchParams.get('stripe') === 'success') {
      toast.success('Payment complete — refreshing membership status…')
      window.location.href = '/payment'
    }
  }, [searchParams])

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get('/api/plans')
        setPlans(
          res.data.map((p, i) => ({
            ...p,
            ...PLAN_UI[i % PLAN_UI.length],
            cta: `Subscribe — ¥${p.price}/mo`,
          }))
        )
      } catch {
        toast.error('Failed to load plans')
      }
    }
    load()
  }, [])

  useEffect(() => {
    if (localStorage.getItem('token') && !user) refreshUser?.()
  }, [user, refreshUser])

  const startPlanPayment = async (plan) => {
    if (plan.price > 200000) {
      toast.error('Amount exceeds ¥200,000. Please use corporate bank transfer and contact RotationStreet support.', { duration: 8000 })
      return
    }
    setLoading(true)
    try {
      const res = await axios.post('/api/payments/static-qr/create', {
        amountYuan: plan.price,
        currency: 'CNY',
        plan: plan.id,
        role,
        periodMonths: 1,
        lineItems: [{ label: `${plan.name} Plan`, value: plan.price, periodMonths: 1, periodLabel: '1 month' }],
        packageSummary: {
          tierLabel: plan.name,
          subscriptionPeriodMonths: 1,
          role,
          selections: [`${plan.name} Plan (1 month)`],
        },
      })
      setPayOrder(res.data)
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to create payment order')
    } finally {
      setLoading(false)
    }
  }

  return (
    <PageShell>
      <div className={isPortal ? 'portal-payment-page portal-page-full' : ''}>
        {isPortal ? (
          <PortalHeader title="Upgrade Plan" showSearch={false} />
        ) : (
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-navy">Pricing & Payment</h1>
            <p className="text-gray-500 text-sm mt-1">
              {isPaid
                ? `Current plan: ${user?.plan_tier?.charAt(0).toUpperCase() + user?.plan_tier?.slice(1)}`
                : 'Choose a plan to unlock full platform features'}
            </p>
          </div>
        )}

        <div className={isPortal ? 'portal-payment-enterprise-notice' : 'mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950'}>
          <p className={isPortal ? 'portal-payment-enterprise-notice-en' : 'text-amber-800'}>
            The upgrade plan is only available for enterprise subscriptions. If you are an individual user and need to subscribe, please contact customer service via WeChat: 13552999354
          </p>
        </div>

        {isPortal && (
          <p className="portal-payment-subtitle">
            {role === 'Startup'
              ? 'Startup pricing — connect with global LPs and GPs'
              : isPaid
                ? `Current plan: ${user?.plan_tier?.charAt(0).toUpperCase() + user?.plan_tier?.slice(1)}`
                : 'Choose a plan to unlock full platform features'}
          </p>
        )}

        {isPaid && (
          isPortal ? (
            <div className="portal-status-banner">
              <div className="icon"><RsIcon name="check" size={20} className="w-5 h-5" /></div>
              <div>
                <p className="font-semibold text-green-800">Active Subscription</p>
                <p className="text-sm text-green-600">
                  {user?.plan_tier} plan · Renews on {formatRenewDate(user?.current_period_end)}
                </p>
              </div>
            </div>
          ) : (
          <div className="card p-5 mb-8 bg-green-50 border-green-200">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center">
                <RsIcon name="check" size={20} className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="font-semibold text-green-800">Active Subscription</p>
                <p className="text-sm text-green-600">
                  {user?.plan_tier} plan • Renews on {formatRenewDate(user?.current_period_end)}
                </p>
              </div>
            </div>
          </div>
          )
        )}

        {useUpgradeBuilder ? (
          <UpgradePricingBuilder role={role} onPaid={() => refreshUser?.()} />
        ) : (
        <>
        <div className={isPortal ? 'portal-pricing-grid' : 'grid grid-cols-3 gap-6 mb-8'}>
          {plans.map((plan) => (
            <div
              key={plan.id}
              className={isPortal ? `portal-pricing-card${plan.popular ? ' popular' : ''}` : `card p-6 relative ${plan.popular ? 'ring-2 ring-gold-500 shadow-lg' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-gold-500 text-white text-xs font-bold rounded-full">
                  MOST POPULAR
                </div>
              )}
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-xl bg-${plan.color}-50 flex items-center justify-center`}>
                  <RsIcon name={plan.icon} size={20} className={`w-5 h-5 text-${plan.color}-600`} />
                </div>
                <div>
                  <h3 className="font-bold text-navy">{plan.name}</h3>
                  <p className="text-xs text-gray-500">Monthly billing</p>
                </div>
              </div>
              <div className="mb-6">
                <span className="text-3xl font-extrabold text-navy">¥{plan.price.toLocaleString()}</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                    <RsIcon name="check" size={16} className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => startPlanPayment(plan)}
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
                  plan.popular
                    ? 'bg-gold-500 text-white hover:bg-gold-600'
                    : 'bg-primary-50 text-primary-600 hover:bg-primary-100'
                }`}
              >
                {plan.cta}
              </button>
            </div>
          ))}
        </div>

        <div className={isPortal ? 'portal-comparison-table' : 'card p-6'}>
          <h3 className="text-lg font-bold text-navy mb-4">Feature Comparison</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left p-3 text-sm font-medium text-gray-500">Feature</th>
                  <th className="text-center p-3 text-sm font-medium text-gray-500">Free</th>
                  <th className="text-center p-3 text-sm font-medium text-gray-500">Basic</th>
                  <th className="text-center p-3 text-sm font-medium text-gray-500">Premium</th>
                  <th className="text-center p-3 text-sm font-medium text-gray-500">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {buildComparisonRows(plans).map((row, idx) => (
                  <tr key={idx} className="border-b border-gray-100">
                    <td className="p-3 text-sm text-navy">{row.feature}</td>
                    <td className="p-3 text-center text-sm text-gray-500">{row.free}</td>
                    <td className="p-3 text-center text-sm text-gray-500">{row.basic}</td>
                    <td className="p-3 text-center text-sm text-green-600 font-medium">{row.premium}</td>
                    <td className="p-3 text-center text-sm text-green-600 font-medium">{row.enterprise}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </>
        )}
      </div>

      {payOrder && (
        <StaticWeChatPayModal
          order={payOrder}
          onClose={() => setPayOrder(null)}
          onSuccess={() => {
            setPayOrder(null)
          }}
        />
      )}
    </PageShell>
  )
}
